import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi } from '@tunasong/redux'
import type { RuleFiringBatchEntity } from '@tunasong/schemas'
import { AutomationsIndex, describeRule, readRules, resumeRule, RuleHistory, setRuleEnabled } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'

/**
 * Oversight surface for automations: every rule, plus what the selected one has
 * actually done.
 *
 * Rules are read from the profile because that is the only entity that can host
 * them today. The entity model allows any entity to be a host, and when a second
 * host type ships this enumeration has to change — there is no way to query
 * "every entity carrying rules" without an index.
 */
export const AutomationsPage: FC = () => {
  const { profile, isLoading: profileLoading } = graphHooks.useProfile()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>(undefined)

  const config = profile?.config
  const rules = readRules(config)

  // Firing batches are persisted as children of the rule's host entity.
  const { entities, isSuccess: historyLoaded } = graphHooks.useEntityChildren<RuleFiringBatchEntity>({
    parentId: profile?.id,
    filter: 'rule-firing-batch',
    skip: !profile?.id,
  })

  const handleToggle = (ruleId: string, enabled: boolean) => {
    if (!profile) {
      return
    }
    void updateProfile({ id: profile.id, profile: { config: setRuleEnabled(config, ruleId, enabled) } })
  }

  const handleResume = (ruleId: string) => {
    if (!profile) {
      return
    }
    void updateProfile({ id: profile.id, profile: { config: resumeRule(config, ruleId) } })
  }

  // "Last ran" comes from the batches: rule.lastFiredAt is never written.
  const lastFiredByRuleId: Record<string, string> = {}
  for (const batch of entities) {
    const current = lastFiredByRuleId[batch.ruleId]
    if (!current || batch.firedAt > current) {
      lastFiredByRuleId[batch.ruleId] = batch.firedAt
    }
  }

  const batches = selectedRuleId ? entities.filter(batch => batch.ruleId === selectedRuleId) : []
  const selectedRule = selectedRuleId ? rules.find(rule => rule.id === selectedRuleId) : undefined

  // While the profile itself is still loading, `rules` reads as `[]` -- that is indistinguishable
  // from "you truly have none", so render a neutral loading state instead of asserting the latter.
  if (profileLoading) {
    return (
      <section aria-label="Automations">
        <h1>Automations</h1>
        <p>Loading automations…</p>
      </section>
    )
  }

  // Loading finished but there is still no profile -- an error, not "you have none". `rules` would
  // again read as `[]` here, so without this branch the page would assert the same false negative
  // AutomationsIndex's own empty state already guards against for the loading case.
  if (!profile) {
    return (
      <section aria-label="Automations">
        <h1>Automations</h1>
        <p>Couldn&apos;t load your automations. Try reloading the page.</p>
      </section>
    )
  }

  return (
    <section aria-label="Automations">
      <h1>Automations</h1>
      <AutomationsIndex
        rules={rules}
        lastFiredByRuleId={lastFiredByRuleId}
        historyLoading={!historyLoaded}
        onToggle={handleToggle}
        onSelect={setSelectedRuleId}
        onResume={handleResume}
      />
      {selectedRule ? (
        <>
          <h2>{describeRule(selectedRule).actionLabel}</h2>
          <RuleHistory batches={batches} loading={!historyLoaded} />
        </>
      ) : null}
    </section>
  )
}

export default AutomationsPage
